import React, { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

/**
 * Inline multi-track audio player for self-hosted release tracks.
 * Props:
 *   tracks: [{ title, audio_url }]
 */
export default function AudioPlayer({ tracks }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.currentTime);
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => {
      if (current < tracks.length - 1) {
        setCurrent((c) => c + 1);
      } else {
        setPlaying(false);
        setProgress(0);
      }
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [current, tracks.length]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.play().catch(() => setPlaying(false));
    else a.pause();
  }, [playing, current]);

  if (!tracks || tracks.length === 0) return null;
  const t = tracks[current];
  // Forward compat: preview_url is the new field; audio_url is the legacy field
  const streamUrl = t?.preview_url || t?.audio_url || "";

  const fmt = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    a.currentTime = ratio * duration;
  };

  return (
    <div className="border" style={{ borderColor: "var(--upr-border)", background: "var(--upr-surface)" }} data-testid="audio-player">
      <audio ref={audioRef} src={streamUrl} preload="metadata" />
      <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: "var(--upr-border)" }}>
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          data-testid="audio-toggle"
          className="w-10 h-10 inline-flex items-center justify-center"
          style={{ background: "var(--upr-primary)", color: "#fff" }}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-extrabold text-sm truncate">{t.title || `Track ${current + 1}`}</div>
          <div className="text-xs" style={{ color: "var(--upr-muted)" }}>
            {fmt(progress)} / {fmt(duration)}
          </div>
        </div>
      </div>
      <div className="px-4 py-2 cursor-pointer" onClick={seek}>
        <div className="h-1 w-full" style={{ background: "var(--upr-border)" }}>
          <div className="h-1" style={{ width: `${duration ? (progress / duration) * 100 : 0}%`, background: "var(--upr-primary)" }} />
        </div>
      </div>
      <ol className="border-t" style={{ borderColor: "var(--upr-border)" }}>
        {tracks.map((tk, i) => (
          <li key={i}>
            <button
              type="button"
              onClick={() => { setCurrent(i); setPlaying(true); }}
              data-testid={`audio-track-${i}`}
              className="w-full flex items-center justify-between px-4 py-2 text-left transition-colors hover:bg-[color:var(--upr-bg)]"
              style={{ background: i === current ? "var(--upr-bg)" : "transparent" }}
            >
              <span className="flex items-center gap-3">
                <span className="font-heading font-extrabold text-xs w-6 text-right" style={{ color: "var(--upr-muted)" }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className={"font-body text-sm " + (i === current ? "font-bold" : "")}>{tk.title || `Track ${i + 1}`}</span>
              </span>
              {i === current && playing && <span className="upr-eyebrow text-[color:var(--upr-primary)]" style={{ fontSize: "0.6rem" }}>Playing</span>}
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
